import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { Search, Edit3, X, Check, ShieldAlert, BadgeInfo, Users, Trash2, AlertTriangle, ChevronRight, Activity, Wallet, CreditCard as CardIcon, Database as DBIcon, TrendingUp, Package } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const { toast } = useToast();

    // Selection & Deletion State
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [usersToDelete, setUsersToDelete] = useState(null);
    const [deleteSubaccountsList, setDeleteSubaccountsList] = useState([]);
    const [selectedAccountsToDelete, setSelectedAccountsToDelete] = useState([]);

    // Modal Edit State
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({
        is_active: true,
        payment_status: 'trial',
        trial_end_date: '',
        subscription_end_date: '',
        notes: '',
        subaccounts_limit: 0,
        sync_expiration: false,
        sales_plan: null,
        sales_limit: null,
    });
    
    // Subaccounts State
    const [userSubaccounts, setUserSubaccounts] = useState([]);
    const [subaccountsLoading, setSubaccountsLoading] = useState(false);
    const [subaccountToDelete, setSubaccountToDelete] = useState(null);

    // Drawer State
    const [selectedUserForDrawer, setSelectedUserForDrawer] = useState(null);
    const [drawerData, setDrawerData] = useState(null);
    const [drawerLoading, setDrawerLoading] = useState(false);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase.rpc('admin_get_all_users');
            if (error) throw error;
            setUsers(data || []);
        } catch (err) {
            console.error("Erro carregando usuários:", err);
            toast({
                variant: "destructive",
                title: "Erro ao Carregar",
                description: "Não foi possível buscar a lista de clientes."
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const handleSaveEdit = async () => {
        try {
            // Calcular o sales_limit baseado no plano selecionado
            let finalSalesLimit = -1; // -1 = não alterar (sentinel do backend)
            let finalSalesPlan = '[NO_CHANGE]';

            if (editForm.sales_plan !== undefined) {
                finalSalesPlan = editForm.sales_plan; // pode ser null, 'basic', etc.
                
                switch (editForm.sales_plan) {
                    case 'basic': finalSalesLimit = 10000; break;
                    case 'master':
                    case 'intermediate': finalSalesLimit = 20000; break;
                    case 'premium':
                    case 'advanced': finalSalesLimit = 50000; break;
                    case 'custom': finalSalesLimit = parseInt(editForm.sales_limit) || 0; break;
                    case null: finalSalesLimit = 0; break; // 0 será convertido em NULL pelo NULLIF no backend
                    default: finalSalesLimit = -1;
                }
            }

            const { data, error } = await supabase.rpc('admin_update_user_subscription', {
                p_user_id: editingUser.user_id,
                p_is_active: editForm.is_active,
                p_payment_status: editForm.payment_status,
                p_trial_end_date: editForm.trial_end_date ? new Date(editForm.trial_end_date).toISOString() : null,
                p_subscription_end_date: editForm.subscription_end_date ? new Date(editForm.subscription_end_date).toISOString() : null,
                p_notes: editForm.notes,
                p_sales_plan: finalSalesPlan,
                p_sales_limit: finalSalesLimit,
            });

            if (error) throw error;
            if (editForm.sync_expiration && userSubaccounts.length > 0) {
                // Atualizar o expiration_date de todas as subcontas no backend
                // No futuro podemos criar um RPC específico, por hora a lógica reside na conta
                toast({ title: 'Vencimentos vinculados', description: 'Em breve a API aplicará a todas as contas.'});
            }
            
            toast({
                title: "Sucesso",
                description: "Dados do usuário atualizados."
            });
            setEditingUser(null);
            loadUsers(); // Recarrega lista
        } catch (err) {
            console.error(err);
            toast({
                variant: 'destructive',
                title: 'Falha na Atualização',
                description: err.message || 'Erro inesperado.'
            });
        }
    };

    const fetchSubaccounts = async (userId) => {
        setSubaccountsLoading(true);
        try {
            const { data, error } = await supabase.rpc('admin_get_subaccounts', { p_parent_user_id: userId });
            if (!error && data) {
                setUserSubaccounts(data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setSubaccountsLoading(false);
        }
    };

    const openEdit = (user) => {
        setEditingUser(user);
        setEditForm({
            is_active: user.is_active !== false,
            payment_status: user.payment_status || 'trial',
            trial_end_date: formatDateInput(user.trial_end_date),
            subscription_end_date: formatDateInput(user.subscription_end_date),
            notes: user.notes || '',
            sync_expiration: false,
            sales_plan: user.sales_plan || null,
            sales_limit: user.sales_limit || null,
        });
        if (!user.company_name) {
            fetchSubaccounts(user.user_id);
        } else {
            setUserSubaccounts([]);
        }
    };

    const toggleTalked = async (u) => {
        try {
            const { error } = await supabase.rpc('admin_update_user_subscription', {
                p_user_id: u.user_id,
                p_is_active: u.is_active,
                p_payment_status: u.payment_status,
                p_trial_end_date: u.trial_end_date,
                p_subscription_end_date: u.subscription_end_date,
                p_has_talked: !u.has_talked
            });
            if (error) throw error;
            loadUsers();
        } catch (err) {
            console.error("Erro ao atualizar status de conversa:", err);
            toast({
                variant: 'destructive',
                title: 'Erro',
                description: 'Não foi possível atualizar o status.'
            });
        }
    };

    const handleDeleteSingleSubaccount = (sub) => {
        setSubaccountToDelete(sub);
    };

    const confirmDeleteSingleSubaccount = async () => {
        if (!subaccountToDelete) return;
        try {
            const { error } = await supabase.rpc('admin_delete_subaccount', { p_subaccount_id: subaccountToDelete.id });
            if (error) throw error;
            
            toast({
                title: "Sucesso",
                description: "Subconta apagada com sucesso."
            });
            
            setUserSubaccounts(prev => prev.filter(s => s.id !== subaccountToDelete.id));
            loadUsers();
        } catch (err) {
            console.error(err);
            toast({
                variant: 'destructive',
                title: 'Falha ao deletar',
                description: err.message || 'Erro inesperado.'
            });
        } finally {
            setSubaccountToDelete(null);
        }
    };

    const formatDateInput = (isoDateString) => {
        if (!isoDateString) return '';
        const d = new Date(isoDateString);
        if (isNaN(d)) return '';
        const tzOffset = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
    };

    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2
        }).format(valor || 0);
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    const openUserDrawer = async (u) => {
        setSelectedUserForDrawer(u);
        setDrawerLoading(true);
        setDrawerData(null);
        try {
            const { data, error } = await supabase.rpc('admin_get_user_analytics', {
                p_user_id: u.user_id
            });
            if (error) throw error;
            setDrawerData(data);
        } catch (err) {
            console.error("Erro ao buscar analytics:", err);
            toast({
                variant: 'destructive',
                title: 'Erro',
                description: 'Não foi possível carregar os detalhes do usuário.'
            });
        } finally {
            setDrawerLoading(false);
        }
    };

    const openEditModal = (u) => {
        setEditingUser(u);
        setEditForm({
            is_active: u.is_active,
            payment_status: u.payment_status,
            trial_end_date: formatDateInput(u.trial_end_date),
            subscription_end_date: formatDateInput(u.subscription_end_date),
            notes: u.notes || '',
            subaccounts_limit: u.subaccounts_limit || 0
        });
    };

    // --- Selection and Deletion Logic ---
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedUserIds(filteredUsers.map(u => u.user_id));
        } else {
            setSelectedUserIds([]);
        }
    };

    const handleSelectUser = (userId) => {
        setSelectedUserIds(prev => 
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const executeDeletion = async () => {
        if (!usersToDelete || usersToDelete.length === 0) return;
        
        try {
            // Se for exclusão mútua (usuário com subcontas, exclusão parcial)
            if (usersToDelete.length === 1 && deleteSubaccountsList.length > 0) {
                const mainUserId = usersToDelete[0].user_id;
                
                // Se a principal não foi selecionada, deletamos apenas as subcontas selecionadas
                if (!selectedAccountsToDelete.includes(mainUserId)) {
                    const { error } = await supabase.from('subaccounts').delete().in('id', selectedAccountsToDelete);
                    if (error) throw error;
                    toast({ title: 'Sucesso', description: `${selectedAccountsToDelete.length} subconta(s) apagada(s).` });
                    setUsersToDelete(null);
                    setSelectedUserIds([]);
                    loadUsers();
                    return;
                }
                // Se a principal FOI selecionada, o admin_delete_users (ou CASCADE) deleta tudo
            }

            const { error } = await supabase.rpc('admin_delete_users', {
                p_user_ids: usersToDelete.map(u => u.user_id)
            });
            if (error) throw error;

            toast({
                title: "Sucesso",
                description: `${usersToDelete.length} usuário(s) apagado(s) permanentemente.`
            });
            setUsersToDelete(null);
            setSelectedUserIds([]);
            loadUsers();
        } catch (err) {
            console.error(err);
            toast({
                variant: 'destructive',
                title: 'Erro ao Apagar',
                description: err.message || 'Ocorreu um erro ao excluir usuários.'
            });
        }
    };

    const openDeleteModal = async (usersToDel) => {
        setUsersToDelete(usersToDel);
        setDeleteSubaccountsList([]);
        setSelectedAccountsToDelete([]);
        if (usersToDel.length === 1 && usersToDel[0].subaccounts_count > 0) {
            try {
                const { data, error } = await supabase.rpc('admin_get_subaccounts', { p_parent_user_id: usersToDel[0].user_id });
                if (!error && data) {
                    setDeleteSubaccountsList(data);
                }
            } catch(e) {}
        }
    };

    const filteredUsers = users.filter(u => 
        (u.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.user_email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Users className="w-6 h-6 text-amber-500" />
                        Gestão de Usuários
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">Busque e edite acessos dos seus clientes.</p>
                </div>
                
                <div className="flex items-center gap-4 w-full md:w-auto">
                    {selectedUserIds.length > 0 && (
                        <button 
                            onClick={() => openDeleteModal(users.filter(u => selectedUserIds.includes(u.user_id)))}
                            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium shadow-lg shadow-red-500/20 flex items-center gap-2 transition whitespace-nowrap"
                        >
                            <Trash2 className="w-4 h-4" />
                            Deletar Selecionados ({selectedUserIds.length})
                        </button>
                    )}

                    <div className="relative flex-1 md:w-80">
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500" />
                        <input 
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2 pl-10 pr-4 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 placeholder-slate-500"
                            placeholder="Buscar por nome ou e-mail..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-xl overflow-x-auto">
                {loading ? (
                    <div className="p-10 text-center text-slate-400">Carregando usuários...</div>
                ) : (
                    <table className="w-full text-left text-sm text-slate-300">
                        <thead className="bg-slate-900/50 text-slate-100 uppercase text-xs">
                            <tr>
                                <th className="px-4 py-4 w-10">
                                    <input 
                                        type="checkbox"
                                        className="w-4 h-4 rounded border-slate-600 text-amber-500 focus:ring-amber-500 bg-slate-900"
                                        checked={filteredUsers.length > 0 && selectedUserIds.length === filteredUsers.length}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th className="px-4 py-4 font-medium">Usuário</th>
                                <th className="px-3 py-4 font-medium">Status</th>
                                <th className="px-3 py-4 font-medium">Cadastro</th>
                                <th className="px-3 py-4 font-medium">Teste</th>
                                <th className="px-3 py-4 font-medium">Assinatura</th>
                                <th className="px-3 py-4 font-medium">Acesso</th>
                                <th className="px-3 py-4 font-medium">Plano</th>
                                <th className="px-3 py-4 font-medium text-center">Conversei</th>
                                <th className="px-4 py-4 font-medium text-right whitespace-nowrap">Ação</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {filteredUsers.length === 0 ? (
                                <tr><td colSpan="10" className="p-8 text-center">Nenhum usuário encontrado.</td></tr>
                            ) : null}
                            {filteredUsers.map(u => (
                                <tr key={u.user_id} className="hover:bg-slate-700/20 transition-colors">
                                    <td className="px-4 py-4">
                                        <input 
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-slate-600 text-amber-500 focus:ring-amber-500 bg-slate-800"
                                            checked={selectedUserIds.includes(u.user_id)}
                                            onChange={() => handleSelectUser(u.user_id)}
                                        />
                                    </td>
                                    <td className="px-4 py-4">
                                        <button 
                                            onClick={() => openUserDrawer(u)} 
                                            className="text-left hover:bg-slate-700/50 p-1.5 -ml-1.5 rounded-lg transition-colors group w-full"
                                            title="Ver Painel Detalhado"
                                        >
                                            <div className="font-semibold text-white group-hover:text-amber-400 transition-colors flex items-center gap-1">
                                                {u.user_name || 'Sem Nome'}
                                                <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                            <div className="text-[11px] text-slate-400 mt-0.5">{u.user_email}</div>
                                            {u.user_phone && (
                                                <div className="text-[11px] text-slate-400 mt-0.5">{u.user_phone}</div>
                                            )}
                                        </button>
                                    </td>
                                    <td className="px-3 py-4">
                                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider
                                            ${u.payment_status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : ''}
                                            ${u.payment_status === 'trial' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : ''}
                                            ${u.payment_status === 'expired' ? 'bg-red-500/10 text-red-400 border border-red-500/20' : ''}
                                        `}>
                                            {u.payment_status}
                                        </span>
                                    </td>
                                    <td className="px-3 py-4 text-slate-400 text-xs">
                                        {new Date(u.created_at).toLocaleDateString('pt-BR')}
                                    </td>
                                    <td className="px-3 py-4 text-xs">
                                        {u.trial_end_date ? new Date(u.trial_end_date).toLocaleDateString('pt-BR') : '-'}
                                    </td>
                                    <td className="px-3 py-4 text-xs">
                                        {u.subscription_end_date ? new Date(u.subscription_end_date).toLocaleDateString('pt-BR') : '-'}
                                    </td>
                                    <td className="px-3 py-4">
                                        {u.is_active ? 
                                            <span className="flex items-center gap-1 text-emerald-400 text-xs font-semibold"><Check className="w-4 h-4" /> Liberado</span> : 
                                            <span className="flex items-center gap-1 text-red-400 text-xs font-semibold"><X className="w-4 h-4" /> Bloqueado</span>
                                        }
                                    </td>
                                    <td className="px-3 py-4">
                                        {u.sales_plan ? (
                                            <div>
                                                <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider
                                                    ${u.sales_plan === 'basic' ? 'bg-sky-500/10 text-sky-400 border border-sky-500/20' : ''}
                                                    ${u.sales_plan === 'master' || u.sales_plan === 'intermediate' ? 'bg-violet-500/10 text-violet-400 border border-violet-500/20' : ''}
                                                    ${u.sales_plan === 'premium' || u.sales_plan === 'advanced' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : ''}
                                                    ${u.sales_plan === 'custom' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : ''}
                                                `}>
                                                    {u.sales_plan === 'basic' ? 'Básico' : (u.sales_plan === 'master' || u.sales_plan === 'intermediate') ? 'Master' : (u.sales_plan === 'premium' || u.sales_plan === 'advanced') ? 'Premium' : 'Personalizado'}
                                                </span>
                                                <div className="text-[10px] text-slate-500 mt-1">
                                                    {(u.sales_count || 0).toLocaleString('pt-BR')}/{(u.sales_limit || 0).toLocaleString('pt-BR')}
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-slate-600">—</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-4 text-center">
                                        <button 
                                            onClick={() => toggleTalked(u)}
                                            className={`transition-all duration-200 transform active:scale-95`}
                                        >
                                            <div className={`w-10 h-6 rounded-full p-1 flex items-center transition-colors ${u.has_talked ? 'bg-emerald-600' : 'bg-red-600'}`}>
                                                <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform duration-200 ${u.has_talked ? 'translate-x-4' : 'translate-x-0'}`} />
                                            </div>
                                        </button>
                                    </td>
                                    <td className="px-4 py-4 text-right whitespace-nowrap">
                                        <div className="flex justify-end gap-2">
                                            <button 
                                                onClick={() => openEdit(u)}
                                                className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-white"
                                                title="Editar Usuário"
                                            >
                                                <Edit3 className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={() => openDeleteModal([u])}
                                                className="p-2 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                                                title="Deletar Usuário"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal de Edição (Somente visível se existir editingUser) */}
            {editingUser && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b border-slate-800 bg-slate-800/50 flex items-center justify-between">
                            <div>
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <ShieldAlert className="w-5 h-5 text-amber-500" />
                                    Editar Permissões
                                </h3>
                                <p className="text-sm text-slate-400 mt-1">{editingUser.user_email}</p>
                            </div>
                            <button onClick={() => setEditingUser(null)} className="text-slate-400 hover:text-white p-2">✕</button>
                        </div>
                        
                        <div className="p-6 overflow-auto space-y-6">
                            
                            {/* Controle de Acesso Master */}
                            <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        className="w-5 h-5 rounded border-slate-600 text-amber-500 focus:ring-amber-500 focus:ring-offset-slate-800 bg-slate-900" 
                                        checked={editForm.is_active}
                                        onChange={e => setEditForm({...editForm, is_active: e.target.checked})}
                                    />
                                    <div>
                                        <div className="text-white font-medium">Acesso Liberado ao Sistema</div>
                                        <p className="text-xs text-slate-400">Se desmarcado, o usuário é bloqueado instantaneamente no Painel.</p>
                                    </div>
                                </label>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-300">Status de Pagamento</label>
                                    <select 
                                        className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                                        value={editForm.payment_status}
                                        onChange={e => setEditForm({...editForm, payment_status: e.target.value})}
                                    >
                                        <option value="trial">Trial (Período de Teste)</option>
                                        <option value="active">Active (Assinante)</option>
                                        <option value="expired">Expired (Inadimplente)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                                    <h4 className="text-amber-400 font-medium text-sm flex items-center gap-2 mb-3">
                                        <BadgeInfo className="w-4 h-4" />
                                        Controle de Vencimento
                                    </h4>
                                     <div className="space-y-4">
                                        {userSubaccounts.length > 0 && (
                                            <div className="flex flex-col gap-2">
                                                <div className="text-white text-sm font-medium mb-1">Subcontas ({userSubaccounts.length})</div>
                                                {userSubaccounts.map(sub => (
                                                    <div key={sub.id} className="flex items-center justify-between bg-slate-900/50 p-3 rounded-lg border border-slate-700">
                                                        <div>
                                                            <div className="text-sm font-bold text-white flex items-center gap-2">
                                                                {sub.company_name}
                                                                <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider
                                                                    ${sub.payment_status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : ''}
                                                                    ${sub.payment_status === 'trial' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : ''}
                                                                    ${sub.payment_status === 'expired' ? 'bg-red-500/10 text-red-400 border border-red-500/20' : ''}
                                                                `}>
                                                                    {sub.payment_status || 'trial'}
                                                                </span>
                                                                {sub.sales_plan && (
                                                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-sky-500/10 text-sky-400 border border-sky-500/20">
                                                                        {sub.sales_plan === 'basic' ? 'Básico' : (sub.sales_plan === 'master' || sub.sales_plan === 'intermediate') ? 'Master' : (sub.sales_plan === 'premium' || sub.sales_plan === 'advanced') ? 'Premium' : `${(sub.sales_limit || 0) / 1000}k`}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-slate-400 mt-1 flex items-center gap-3">
                                                                <span>Trial: {sub.trial_end_date ? new Date(sub.trial_end_date).toLocaleDateString('pt-BR') : '-'}</span>
                                                                {sub.sales_limit ? (
                                                                    <span>Vendas: {(sub.total_sales || 0).toLocaleString('pt-BR')} / {(sub.sales_limit || 0).toLocaleString('pt-BR')}</span>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    openEdit({...sub, user_id: sub.id, user_email: sub.company_name, has_talked: false});
                                                                }}
                                                                className="px-3 py-1.5 text-xs bg-amber-500/10 text-amber-500 hover:bg-amber-500 hover:text-slate-900 border border-amber-500/20 rounded-md transition-colors"
                                                            >
                                                                Editar Plano
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    handleDeleteSingleSubaccount(sub);
                                                                }}
                                                                className="p-1.5 text-red-400 bg-red-500/10 hover:bg-red-500 hover:text-white border border-red-500/20 rounded-md transition-colors"
                                                                title="Deletar Subconta permanentemente"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div>
                                            <label className="text-xs text-slate-400 block mb-1">Vencimento do Trial</label>
                                            <input 
                                                type="datetime-local" 
                                                className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-white text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                                                value={editForm.trial_end_date}
                                                onChange={e => setEditForm({...editForm, trial_end_date: e.target.value})}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs text-slate-400 block mb-1">Vencimento da Assinatura Anual</label>
                                            <input 
                                                type="datetime-local" 
                                                className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-white text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                                                value={editForm.subscription_end_date}
                                                onChange={e => setEditForm({...editForm, subscription_end_date: e.target.value})}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-300">Observações</label>
                                    <textarea 
                                        rows="3"
                                        placeholder="Anotações internas sobre este usuário..."
                                        className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 resize-none"
                                        value={editForm.notes}
                                        onChange={e => setEditForm({...editForm, notes: e.target.value})}
                                    ></textarea>
                                </div>

                                {/* Bloco: Plano Escolhido (Controle de Vendas) */}
                                <div className="p-4 bg-sky-500/10 border border-sky-500/20 rounded-lg">
                                    <h4 className="text-sky-400 font-medium text-sm flex items-center gap-2 mb-3">
                                        <Package className="w-4 h-4" />
                                        Plano de Vendas
                                    </h4>
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-2 gap-2">
                                            {[
                                                { value: null, label: 'Sem Plano', sublabel: 'Ilimitado' },
                                                { value: 'basic', label: 'Básico', sublabel: '10.000 vendas — R$ 79' },
                                                { value: 'master', label: 'Master', sublabel: '20.000 vendas — R$ 99' },
                                                { value: 'premium', label: 'Premium', sublabel: '50.000 vendas — R$ 159' },
                                            ].map(plan => (
                                                <label 
                                                    key={plan.value || 'none'} 
                                                    className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-all
                                                        ${editForm.sales_plan === plan.value 
                                                            ? 'bg-sky-500/20 border-sky-500/50 ring-1 ring-sky-500/30' 
                                                            : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                                                        }
                                                    `}
                                                >
                                                    <input 
                                                        type="radio" 
                                                        name="sales_plan" 
                                                        className="mt-1 text-sky-500 focus:ring-sky-500 bg-slate-900 border-slate-600"
                                                        checked={editForm.sales_plan === plan.value || (plan.value === 'master' && editForm.sales_plan === 'intermediate') || (plan.value === 'premium' && editForm.sales_plan === 'advanced')}
                                                        onChange={() => setEditForm({...editForm, sales_plan: plan.value, sales_limit: plan.value === 'basic' ? 10000 : plan.value === 'master' ? 20000 : plan.value === 'premium' ? 50000 : plan.value === null ? null : editForm.sales_limit})}
                                                    />
                                                    <div>
                                                        <div className="text-white text-sm font-medium">{plan.label}</div>
                                                        <div className="text-[10px] text-slate-400">{plan.sublabel}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>

                                        {/* Opção Personalizado */}
                                        <label 
                                            className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-all
                                                ${editForm.sales_plan === 'custom' 
                                                    ? 'bg-sky-500/20 border-sky-500/50 ring-1 ring-sky-500/30' 
                                                    : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                                                }
                                            `}
                                        >
                                            <input 
                                                type="radio" 
                                                name="sales_plan" 
                                                className="mt-1 text-sky-500 focus:ring-sky-500 bg-slate-900 border-slate-600"
                                                checked={editForm.sales_plan === 'custom'}
                                                onChange={() => setEditForm({...editForm, sales_plan: 'custom'})}
                                            />
                                            <div className="flex-1">
                                                <div className="text-white text-sm font-medium">Personalizado</div>
                                                <div className="text-[10px] text-slate-400 mb-2">Defina manualmente a quantidade</div>
                                                {editForm.sales_plan === 'custom' && (
                                                    <input 
                                                        type="number" 
                                                        min="1"
                                                        placeholder="Ex: 15000"
                                                        className="w-full bg-slate-800 border border-slate-600 rounded-md p-2 text-white text-sm focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                                                        value={editForm.sales_limit || ''}
                                                        onChange={e => setEditForm({...editForm, sales_limit: parseInt(e.target.value) || null})}
                                                    />
                                                )}
                                            </div>
                                        </label>

                                        {/* Barra de progresso — visível quando há plano */}
                                        {editForm.sales_plan && editingUser && (
                                            (() => {
                                                const currentCount = editingUser.sales_count || editingUser.total_sales || 0;
                                                const limit = editForm.sales_plan === 'basic' ? 10000 
                                                    : (editForm.sales_plan === 'master' || editForm.sales_plan === 'intermediate') ? 20000 
                                                    : (editForm.sales_plan === 'premium' || editForm.sales_plan === 'advanced') ? 50000 
                                                    : (editForm.sales_limit || 0);
                                                const pct = limit > 0 ? Math.min(Math.round((currentCount / limit) * 100), 100) : 0;
                                                const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-sky-500';
                                                return (
                                                    <div className="mt-2 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                                                        <div className="flex justify-between text-xs mb-1.5">
                                                            <span className="text-slate-400">Vendas atuais</span>
                                                            <span className="text-white font-semibold">
                                                                {currentCount.toLocaleString('pt-BR')} / {limit.toLocaleString('pt-BR')} ({pct}%)
                                                            </span>
                                                        </div>
                                                        <div className="w-full bg-slate-800 rounded-full h-2">
                                                            <div className={`h-2 rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }}></div>
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>
                        
                        <div className="p-6 border-t border-slate-800 flex justify-end gap-3 bg-slate-800/50">
                            <button 
                                onClick={() => setEditingUser(null)}
                                className="px-4 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700 transition"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={handleSaveEdit}
                                className="px-6 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold shadow-lg shadow-amber-500/20 transition"
                            >
                                Salvar Alterações
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Exclusão */}
            {usersToDelete && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-red-500/30 rounded-xl max-w-md w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b border-slate-800 bg-red-500/10 flex items-center gap-3">
                            <div className="p-3 bg-red-500/20 rounded-full text-red-400 flex-shrink-0">
                                <AlertTriangle className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white">Deletar Usuário(s)</h3>
                                <p className="text-sm text-red-400/80">Esta ação é irreversível.</p>
                            </div>
                        </div>
                        
                        <div className="p-6 text-slate-300 space-y-4 overflow-y-auto">
                            {usersToDelete.length === 1 && deleteSubaccountsList.length > 0 ? (
                                <>
                                    <p>O usuário <strong className="text-white">{usersToDelete[0].user_name}</strong> possui <strong className="text-white">{deleteSubaccountsList.length} subconta(s)</strong>.</p>
                                    <p className="text-sm">Selecione quais contas deseja excluir permanentemente:</p>
                                    <div className="space-y-2 mt-4">
                                        <label className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg border border-slate-700 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                className="w-4 h-4 rounded border-slate-600 text-red-500 focus:ring-red-500 bg-slate-900"
                                                checked={selectedAccountsToDelete.includes(usersToDelete[0].user_id)}
                                                onChange={(e) => {
                                                    const id = usersToDelete[0].user_id;
                                                    setSelectedAccountsToDelete(prev => e.target.checked ? [...prev, id] : prev.filter(i => i !== id));
                                                }}
                                            />
                                            <div>
                                                <span className="font-bold text-white">{usersToDelete[0].user_name} (Principal)</span>
                                                <p className="text-xs text-red-400 mt-0.5">Apagar a conta principal apagará todas as subcontas também.</p>
                                            </div>
                                        </label>
                                        
                                        {deleteSubaccountsList.map(sub => (
                                            <label key={sub.id} className={`flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50 cursor-pointer ${selectedAccountsToDelete.includes(usersToDelete[0].user_id) ? 'opacity-50 pointer-events-none' : ''}`}>
                                                <input 
                                                    type="checkbox" 
                                                    className="w-4 h-4 rounded border-slate-600 text-red-500 focus:ring-red-500 bg-slate-900"
                                                    checked={selectedAccountsToDelete.includes(sub.id) || selectedAccountsToDelete.includes(usersToDelete[0].user_id)}
                                                    onChange={(e) => {
                                                        const id = sub.id;
                                                        setSelectedAccountsToDelete(prev => e.target.checked ? [...prev, id] : prev.filter(i => i !== id));
                                                    }}
                                                />
                                                <div>
                                                    <span className="text-slate-200">{sub.company_name} (Subconta)</span>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                    <div className="mt-4 text-right">
                                        <button 
                                            onClick={() => setSelectedAccountsToDelete([usersToDelete[0].user_id, ...deleteSubaccountsList.map(s => s.id)])}
                                            className="text-sm text-red-400 hover:text-red-300 transition-colors"
                                        >
                                            Selecionar Todas
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p>Você está prestes a excluir <strong>{usersToDelete.length}</strong> usuário(s) permanentemente.</p>
                                    <div className="text-sm bg-slate-800 p-3 rounded-lg border border-slate-700 space-y-1">
                                        <strong className="text-red-400">ATENÇÃO:</strong> Isso apagará <strong className="text-white">TUDO</strong> vinculado a {usersToDelete.length === 1 ? 'esta conta' : 'estas contas'}:
                                        <ul className="list-disc pl-5 mt-2 text-slate-400">
                                            <li>Dados de Vendas e Recebimentos</li>
                                            <li>Operadoras, Bandeiras e Formas de Pagamento</li>
                                            <li>Histórico de Assinatura</li>
                                            <li>Login de Acesso</li>
                                        </ul>
                                    </div>
                                    <p className="font-medium text-white">Deseja realmente prosseguir?</p>
                                </>
                            )}
                        </div>
                        
                        <div className="p-6 border-t border-slate-800 flex justify-end gap-3 bg-slate-800/50 flex-shrink-0">
                            <button 
                                onClick={() => { setUsersToDelete(null); setSelectedAccountsToDelete([]); }}
                                className="px-4 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700 transition"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={executeDeletion}
                                disabled={usersToDelete.length === 1 && deleteSubaccountsList.length > 0 && selectedAccountsToDelete.length === 0}
                                className="px-6 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold shadow-lg shadow-red-500/20 transition flex items-center gap-2 disabled:opacity-50"
                            >
                                <Trash2 className="w-4 h-4" />
                                {usersToDelete.length === 1 && deleteSubaccountsList.length > 0 ? (
                                    selectedAccountsToDelete.includes(usersToDelete[0].user_id) ? 'Excluir Conta Principal e Subcontas' : 'Excluir Contas Selecionadas'
                                ) : (
                                    'Sim, Deletar Tudo'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Drawer Lateral - Detalhes do Usuário */}
            {selectedUserForDrawer && (
                <>
                    {/* Backdrop */}
                    <div 
                        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity"
                        onClick={() => setSelectedUserForDrawer(null)}
                    ></div>
                    
                    {/* Drawer Content */}
                    <div className="fixed inset-y-0 right-0 z-[70] w-full max-w-md bg-slate-900 shadow-2xl border-l border-slate-800 flex flex-col transform transition-transform duration-300">
                        
                        {/* Header */}
                        <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-800/30">
                            <div>
                                <h2 className="text-xl font-bold text-white">Detalhes do Usuário</h2>
                                <p className="text-sm text-slate-400">{selectedUserForDrawer.user_email}</p>
                            </div>
                            <button 
                                onClick={() => setSelectedUserForDrawer(null)}
                                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-8">
                            {drawerLoading ? (
                                <div className="flex flex-col items-center justify-center h-40 space-y-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                                    <p className="text-slate-400 text-sm">Buscando inteligência de dados...</p>
                                </div>
                            ) : drawerData ? (
                                <>
                                    {/* Métricas Principais */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700 shadow-lg">
                                            <div className="flex items-center gap-2 text-indigo-400 mb-2">
                                                <TrendingUp className="w-4 h-4" />
                                                <span className="text-xs font-semibold uppercase tracking-wider">Vendas</span>
                                            </div>
                                            <div className="text-xl font-bold text-white truncate" title={formatarMoeda(drawerData.sales_value)}>
                                                {formatarMoeda(drawerData.sales_value)}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-1">{drawerData.sales_qty.toLocaleString('pt-BR')} registros</div>
                                        </div>
                                        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700 shadow-lg">
                                            <div className="flex items-center gap-2 text-emerald-400 mb-2">
                                                <Wallet className="w-4 h-4" />
                                                <span className="text-xs font-semibold uppercase tracking-wider">Recebimentos</span>
                                            </div>
                                            <div className="text-xl font-bold text-white truncate" title={formatarMoeda(drawerData.receipts_value)}>
                                                {formatarMoeda(drawerData.receipts_value)}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-1">{drawerData.receipts_qty.toLocaleString('pt-BR')} parcelas</div>
                                        </div>
                                    </div>

                                    {/* Saúde da Operação */}
                                    <div className="space-y-3">
                                        <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                                            <Activity className="w-4 h-4 text-amber-500" />
                                            Saúde da Operação
                                        </h3>
                                        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 space-y-4">
                                            <div>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-slate-400">Conversão de Recebíveis</span>
                                                    <span className="text-white font-medium">
                                                        {drawerData.sales_value > 0 ? ((drawerData.receipts_value / drawerData.sales_value) * 100).toFixed(1) : 0}%
                                                    </span>
                                                </div>
                                                <div className="w-full bg-slate-900 rounded-full h-2">
                                                    <div 
                                                        className="h-2 rounded-full bg-emerald-500 transition-all" 
                                                        style={{ width: `${Math.min((drawerData.receipts_value / (drawerData.sales_value || 1)) * 100, 100)}%` }}
                                                    ></div>
                                                </div>
                                            </div>

                                            <div className="pt-3 mt-3 flex justify-between items-center border-t border-slate-700">
                                                <span className="text-sm text-slate-400">Última Venda Sincronizada</span>
                                                <span className="text-sm text-white font-medium">
                                                    {drawerData.last_sale_date ? new Date(drawerData.last_sale_date).toLocaleDateString('pt-BR') : 'Sem dados'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Operadoras Configuradas */}
                                    <div className="space-y-3">
                                        <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                                            <CardIcon className="w-4 h-4 text-blue-400" />
                                            Volume por Operadora
                                        </h3>
                                        <div className="bg-slate-800/50 rounded-xl border border-slate-700 divide-y divide-slate-700">
                                            {drawerData.operators_breakdown && drawerData.operators_breakdown.length > 0 ? (
                                                drawerData.operators_breakdown.map((op, idx) => (
                                                    <div key={idx} className="p-3 flex justify-between items-center">
                                                        <div>
                                                            <div className="text-sm font-semibold text-white">{op.empresa}</div>
                                                            <div className="text-xs text-slate-500">{op.qtd.toLocaleString('pt-BR')} transações</div>
                                                        </div>
                                                        <div className="text-sm font-medium text-emerald-400">
                                                            {formatarMoeda(op.valor)}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="p-4 text-center text-slate-500 text-sm">Nenhuma venda registrada.</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Infraestrutura / Peso */}
                                    <div className="space-y-3">
                                        <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                                            <DBIcon className="w-4 h-4 text-slate-400" />
                                            Peso no Banco de Dados
                                        </h3>
                                        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-sm text-slate-400">Tamanho Físico Ocupado</span>
                                                <span className="text-sm font-bold text-white bg-slate-700 px-2 py-1 rounded-md">
                                                    {formatBytes(drawerData.sales_bytes + drawerData.receipts_bytes)}
                                                </span>
                                            </div>
                                            <p className="text-[10px] leading-relaxed text-slate-500">
                                                O limite gratuito do Supabase é de 500MB em disco. 
                                                Este valor representa o espaço exato em Megabytes que este usuário consome.
                                            </p>
                                        </div>
                                    </div>
                                    
                                </>
                            ) : null}
                        </div>
                        
                    </div>
                </>
            )}

            {/* Modal de Exclusão de Subconta Única */}
            {subaccountToDelete && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full shadow-2xl overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-slate-800 bg-red-500/10 flex items-center gap-3">
                            <div className="p-2 bg-red-500/20 rounded-full">
                                <AlertTriangle className="w-6 h-6 text-red-500" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-red-500">
                                    Excluir Subconta
                                </h3>
                                <p className="text-sm text-slate-400">Ação irreversível</p>
                            </div>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            <p className="text-slate-300">
                                Você está prestes a excluir a subconta <strong className="text-white">{subaccountToDelete.company_name}</strong>.
                            </p>
                            <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
                                <p className="text-sm text-slate-400">
                                    <strong className="text-red-400">ATENÇÃO:</strong> Todos os dados vinculados a ela serão deletados permanentemente, incluindo:
                                </p>
                                <ul className="list-disc list-inside text-sm text-slate-500 mt-2 space-y-1">
                                    <li>Vendas processadas</li>
                                    <li>Recebimentos</li>
                                    <li>Operadoras e Taxas</li>
                                    <li>Bandeiras</li>
                                    <li>Assinaturas</li>
                                </ul>
                            </div>
                            <p className="text-sm text-amber-500 font-medium text-center pt-2">
                                Deseja continuar?
                            </p>
                        </div>
                        
                        <div className="p-4 border-t border-slate-800 bg-slate-800/50 flex justify-end gap-3">
                            <button 
                                onClick={() => setSubaccountToDelete(null)}
                                className="px-5 py-2.5 rounded-lg font-medium bg-slate-700 hover:bg-slate-600 text-white transition-colors"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={confirmDeleteSingleSubaccount}
                                className="px-5 py-2.5 rounded-lg font-medium bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-500/20 transition-all active:scale-95 flex items-center gap-2"
                            >
                                <Trash2 className="w-4 h-4" />
                                Sim, Excluir Subconta
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsers;
